import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';

// A deliberately amusing loader for the (blocking, ~1-4 min) AI generation.
const MESSAGES = [
  'Warming up the AI copywriters…',
  'Staging the virtual living room…',
  'Fluffing the throw pillows…',
  'Chasing the best natural lighting…',
  'Negotiating the perfect headline…',
  "Convincing the AI that 'cozy' means 'compact'…",
  'Polishing the marble countertops…',
  'Adding a sea view (budget permitting)…',
  'Brewing fresh, irresistible copy…',
  'Color-grading the sunset shots…',
  'Watering the curb-appeal plants…',
  'Almost there — printing the brochures…',
];

function fmt(sec: number) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function GenerationLoader({ isRegenerate = false }: { isRegenerate?: boolean }) {
  const [msgIndex, setMsgIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const m = setInterval(() => setMsgIndex((i) => (i + 1) % MESSAGES.length), 2800);
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => { clearInterval(m); clearInterval(t); };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6 gap-6">
      {/* Animated emblem: spinning ring + pulsing core */}
      <div className="relative h-28 w-28">
        <div className="absolute inset-0 rounded-full border-4 border-primary/15" />
        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin" />
        <div className="absolute inset-3 rounded-full bg-primary/10 animate-pulse" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Sparkles className="h-10 w-10 text-primary animate-pulse" />
        </div>
      </div>

      {/* Bouncing dots */}
      <div className="flex items-center gap-1.5">
        {[0, 150, 300].map((d) => (
          <span
            key={d}
            className="h-2.5 w-2.5 rounded-full bg-primary/70 animate-bounce"
            style={{ animationDelay: `${d}ms` }}
          />
        ))}
      </div>

      {/* Rotating witty message (re-animates on change via key) */}
      <div className="h-6 flex items-center">
        <p
          key={msgIndex}
          className="text-base font-medium text-foreground animate-in fade-in slide-in-from-bottom-1 duration-500"
        >
          {MESSAGES[msgIndex]}
        </p>
      </div>

      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">
          {isRegenerate ? 'Cooking up another option' : 'Crafting your campaign'} — this can take a couple of minutes.
        </p>
        <p className="text-xs font-mono text-muted-foreground tabular-nums">Elapsed {fmt(elapsed)}</p>
      </div>

      {/* Indeterminate shimmer */}
      <div className="w-full max-w-sm h-1.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full w-2/3 rounded-full bg-gradient-to-r from-transparent via-primary to-transparent animate-pulse" />
      </div>

      <p className="text-xs text-muted-foreground max-w-sm">
        Please keep this tab open — your options will appear here as soon as they're ready.
      </p>
    </div>
  );
}
